import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, Heart, Loader2, Home, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import confetti from 'canvas-confetti';

export default function TipSuccessPage() {
  const [searchParams] = useSearchParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const checkStatus = async () => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      try {
        const response = await api.get(`/payments/status/${sessionId}`);
        setPaymentInfo(response.data);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          // Celebrate!
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 },
            colors: ['#D9381E', '#E07A5F', '#1E4ED8']
          });
        } else if (response.data.payment_status === 'unpaid') {
          setStatus('pending');
        } else {
          setStatus('error');
        }
      } catch (err) {
        setStatus('error');
      }
    };

    checkStatus();
  }, [sessionId, api]);

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full text-center"
      >
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 text-[#1E4ED8] mx-auto mb-4 animate-spin" />
            <h1 className="font-fraunces text-2xl font-bold mb-2">Verification du paiement...</h1>
            <p className="text-gray-500">Merci de patienter</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#4F6F52] to-[#6b8f6e] flex items-center justify-center mx-auto mb-6 shadow-lg">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="font-fraunces text-3xl font-bold mb-3">Merci pour ton soutien!</h1>
            <p className="text-gray-600 mb-6">
              Ton pourboire a ete envoye avec succes. Le createur te remercie!
            </p>
            
            <div className="bg-[#F8F5F2] rounded-2xl p-6 mb-6">
              <Heart className="w-8 h-8 text-[#D9381E] mx-auto mb-3" />
              <p className="text-sm text-gray-500 mb-2">Montant envoye</p>
              <p className="font-fraunces text-4xl font-bold text-[#1E4ED8]">
                {paymentInfo?.amount_total ? (paymentInfo.amount_total / 100).toFixed(2) : '?'} USD
              </p>
              <p className="text-xs text-gray-400 mt-2">+10 BARKA pour toi!</p>
            </div>

            <div className="flex gap-3">
              <Button 
                onClick={() => navigate('/home')}
                className="flex-1 bg-[#1E4ED8] hover:bg-[#1E40AF] text-white rounded-full"
              >
                <Home className="w-4 h-4 mr-2" />
                Accueil
              </Button>
              <Button 
                onClick={() => navigate('/explore')}
                variant="outline"
                className="flex-1 rounded-full"
              >
                Explorer
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </>
        )}

        {status === 'pending' && (
          <>
            <Loader2 className="w-16 h-16 text-yellow-500 mx-auto mb-4 animate-spin" />
            <h1 className="font-fraunces text-2xl font-bold mb-2">Paiement en cours...</h1>
            <p className="text-gray-500 mb-6">Ton paiement est en cours de traitement</p>
            <Button onClick={() => navigate('/home')} className="rounded-full">
              Retour a l'accueil
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 rounded-full bg-[#D9381E]/10 flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">😕</span>
            </div>
            <h1 className="font-fraunces text-2xl font-bold mb-2">Oups!</h1>
            <p className="text-gray-500 mb-6">
              Impossible de verifier le paiement. Si tu as ete debite, contacte le support.
            </p>
            <Button onClick={() => navigate('/support')} className="rounded-full">
              Contacter le support
            </Button>
          </>
        )}
      </motion.div>
    </div>
  );
}
